import { widgetConfig } from "../components/Editor/editor_config";
import { getCssTextObjToString, stringTocamelCase } from "../_helpers/utils";
import { useState } from "react";

const useWidgetAnimation = ({ docContexts }) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const TEXT_ANIMATION_PRESETS = {
    flipInX: {
      animationName: "flipInX",
      charClass: "flip-in-x",
      duration: 0.8,
      ease: "ease-out",
    },
    flipInY: {
      animationName: "flipInY",
      charClass: "flip-letter-y",
      duration: 0.8,
      ease: "ease-out",

      beforeWrap(el) {
        if (!el.parentNode.classList.contains("flip-y-root")) {
          const wrapper = document.createElement("div");
          wrapper.className = "flip-y-root";
          el.parentNode.insertBefore(wrapper, el);
          wrapper.appendChild(el);
        }

        el.parentNode.querySelectorAll(".flip-letter-y").forEach(n => n.remove());

        return el;
      },

      afterRestore(el) {
        const root = el.parentNode;
        if (root && root.classList.contains("flip-y-root")) {
          root.replaceWith(...root.childNodes);
        }
      },
    },
    bounceIn: {
      animationName: "bounceIn",
      charClass: "bounce-in-letter",
      duration: 0.9,
    },
    rotateIn: {
      animationName: "rotateIn",
      charClass: "rotate-in-letter",
      duration: 0.8,
    },
    rollIn: {
      animationName: "rollIn",
      charClass: "roll-in-char",
      duration: 0.8,
    },
    flipOutX: {
      animationName: "flipOutX",
      charClass: "flip-out-char",
      duration: 0.8,
      ease: "ease-in",
    },
    flipOutY: {
      animationName: "flipOutY",
      charClass: "flip-letter-y",
      duration: 0.8,
      ease: "ease-in",

      beforeWrap(el) {
        if (!el.parentNode.classList.contains("flip-y-root")) {
          const wrapper = document.createElement("div");
          wrapper.className = "flip-y-root";
          el.parentNode.insertBefore(wrapper, el);
          wrapper.appendChild(el);
        }

        el.parentNode.querySelectorAll(".flip-letter-y").forEach(n => n.remove());

        return el;
      },

      afterRestore(el) {
        const root = el.parentNode;
        if (root && root.classList.contains("flip-y-root")) {
          root.replaceWith(...root.childNodes);
        }
      },
    },
    bounceOut: {
      animationName: "bounceOut",
      charClass: "bounce-out-letter",
      duration: 0.9,
      ease: "ease-in",
    },
    rotateOut: {
      animationName: "rotateOut",
      charClass: "rotate-out-letter",
      duration: 0.8,
      ease: "ease-in",
    },
    rollOut: {
      animationName: "rollOut",
      charClass: "roll-out-char",
      duration: 0.7,
      ease: "ease-in",
    },
  };

  const handleWidgetEntranceAnimation = idx => {
    document.querySelectorAll(`#dhp-page-${idx} .dhp-widgets`).forEach(wz => {
      if (wz.getAttribute("data-entrance-animation") === "true") {
        let classname = stringTocamelCase(wz.getAttribute("data-entrance-animation-type"));
        let entranceAnimationSpeed =
          widgetConfig.widget_animation.speeds[
            widgetConfig.widget_animation.speeds.findIndex(
              speed => speed.name === wz.getAttribute("data-entrance-animation-speed")
            )
          ].value;
        let delay = parseFloat(entranceAnimationSpeed) * 1000;
        let activeWidgetTransform = docContexts.widgets.find(docwz => docwz.id === wz.id).style.transform;
        setIsAnimating(true);
        handleEntranceAnimation({
          showEntranceAnimation: true,
          element: wz,
          classname,
          activeWidgetTransform,
          entranceAnimationSpeed,
        });
        setTimeout(() => {
          handleEntranceAnimation({
            showEntranceAnimation: false,
            element: wz,
            classname,
            activeWidgetTransform,
            entranceAnimationSpeed,
          });
          setIsAnimating(false);
        }, delay);
      }
    });
  };

  const handleWidgetExitAnimation = idx => {
    let maxDuration = [];
    document.querySelectorAll(`#dhp-page-${idx} .dhp-widgets`).forEach(wz => {
      if (wz.getAttribute("data-exit-animation") === "true") {
        let classname = stringTocamelCase(wz.getAttribute("data-exit-animation-type"));
        let exitAnimationSpeed =
          widgetConfig.widget_animation.speeds[
            widgetConfig.widget_animation.speeds.findIndex(
              speed => speed.name === wz.getAttribute("data-exit-animation-speed")
            )
          ].value;
        let delay = parseFloat(exitAnimationSpeed) * 1000;
        maxDuration.push(delay);
        let activeWidgetTransform = docContexts.widgets.find(docwz => docwz.id === wz.id).style.transform;
        setIsAnimating(true);
        handleExitAnimation({
          showExitAnimation: true,
          element: wz,
          classname,
          activeWidgetTransform,
          exitAnimationSpeed,
        });
        setTimeout(() => {
          handleExitAnimation({
            showExitAnimation: false,
            element: wz,
            classname,
            activeWidgetTransform,
            exitAnimationSpeed,
          });
          setIsAnimating(false);
        }, delay);
      }
    });
    return Math.max(...maxDuration);
  };

  const handleEntranceAnimation = ({
    showEntranceAnimation,
    element,
    classname,
    activeWidgetTransform,
    entranceAnimationSpeed,
  }) => {
    const preset = TEXT_ANIMATION_PRESETS[classname];

    if (showEntranceAnimation) {
      // TEXT ANIMATION
      if (preset) {
        const handled = runTextAnimation({
          element,
          preset,
          durationSec: entranceAnimationSpeed,
          isExit: false,
        });

        if (handled) return; // stop normal flow
      }

      // NORMAL WIDGET ANIMATION
      // prepare child element
      element.classList.add(classname); // Add animation class according to animation type
      element.style.transform = "";
      element.style["animation-duration"] = `${entranceAnimationSpeed}s`; // Add animation dureation according to animation speed

      // create wrapper div
      let parent = element.parentNode;
      let wrapper = document.createElement("div");
      wrapper.style.cssText = getCssTextObjToString({
        transform: activeWidgetTransform,
        position: "absolute",
        width: element.style.width,
        height: element.style.height,
        "z-index": element.style.zIndex,
      });

      parent.replaceChild(wrapper, element); // set the wrapper as child (instead of the element)
      wrapper.appendChild(element); // set element as child of wrapper
    } else {
      if (preset) {
        restoreTextAnimation(element, preset);
        return;
      }

      element.classList.remove(classname); // remove animation class
      element.style.transform = activeWidgetTransform; // add, removed transform property
      element.parentNode.replaceWith(element); // replace with actual div
    }
  };

  const handleExitAnimation = ({
    showExitAnimation,
    element,
    classname,
    activeWidgetTransform,
    exitAnimationSpeed,
  }) => {
    const preset = TEXT_ANIMATION_PRESETS[classname];

    if (showExitAnimation) {
      // TEXT EXIT ANIMATION
      if (preset) {
        const handled = runTextAnimation({
          element,
          preset,
          durationSec: exitAnimationSpeed,
          isExit: true,
        });

        if (handled) return;
      }

      // NORMAL EXIT
      // prepare child element
      element.classList.add(classname); // Add animation class according to animation type
      element.style.transform = "";
      element.style["animation-duration"] = `${exitAnimationSpeed}s`; // Add animation dureation according to animation speed

      // create wrapper div
      let parent = element.parentNode;
      let wrapper = document.createElement("div");
      wrapper.style.cssText = getCssTextObjToString({
        transform: activeWidgetTransform,
        position: "absolute",
        width: element.style.width,
        height: element.style.height,
        "z-index": element.style.zIndex,
      });

      parent.replaceChild(wrapper, element); // set the wrapper as child (instead of the element)
      wrapper.appendChild(element); // set element as child of wrapper
    } else {
      if (preset) {
        restoreTextAnimation(element, preset);
        return;
      }
      element.classList.remove(classname); // remove animation class
      element.style.transform = activeWidgetTransform; // add, removed transform property
      element.parentNode.replaceWith(element); // replace with actual div
    }
  };

  const runTextAnimation = ({ element, preset, durationSec, isExit = false }) => {
    const el = element.querySelector(".primary-text-widget");
    if (!el) return false;

    const originalHTML = el.innerHTML;
    element.setAttribute("data-original-html", originalHTML);

    const context = preset.beforeWrap ? preset.beforeWrap(el) : el;

    const wrapTextNodes = node => {
      [...node.childNodes].forEach(child => {
        if (child.nodeType === 3 && child.textContent.trim().length > 0) {
          const fragment = document.createDocumentFragment();

          const words = child.textContent.split(" ");

          words.forEach((word, wordIndex) => {
            const wordSpan = document.createElement("span");
            wordSpan.style.display = "inline-block"; // 🔥 IMPORTANT
            wordSpan.style.whiteSpace = "nowrap"; // 🔥 PREVENT BREAK

            [...word].forEach(char => {
              const charSpan = document.createElement("span");
              charSpan.className = preset.charClass;
              charSpan.innerHTML = char === " " ? "&nbsp;" : char;
              wordSpan.appendChild(charSpan);
              // animatedCount++;
            });

            fragment.appendChild(wordSpan);

            // add space after word (except last)
            if (wordIndex !== words.length - 1) {
              fragment.appendChild(document.createTextNode(" "));
            }
          });

          child.replaceWith(fragment);
        } else if (child.nodeType === 1) {
          wrapTextNodes(child);
        }
      });
    };

    wrapTextNodes(context);

    const chars = context.querySelectorAll(`.${preset.charClass}`);
    const charCount = chars.length;
    if (!charCount) return false;

    const maxTotalTime = durationSec;
    const animDuration = Math.min(preset.duration, maxTotalTime);

    const perCharDelay = charCount > 1 ? Math.max((maxTotalTime - animDuration) / (charCount - 1), 0) : 0;

    chars.forEach((span, i) => {
      span.style.animation = `
      ${preset.animationName}
      ${animDuration}s
      ${i * perCharDelay}s
      both
      ${preset.ease || (isExit ? "ease-in" : "ease-out")}
    `;
    });

    return true;
  };

  const restoreTextAnimation = (element, preset) => {
    const el = element.querySelector(".primary-text-widget");
    const originalHTML = element.getAttribute("data-original-html");

    if (el && originalHTML) {
      el.innerHTML = originalHTML;

      if (preset.afterRestore) {
        preset.afterRestore(el);
      }

      element.removeAttribute("data-original-html");
    }
  };

  return { handleWidgetEntranceAnimation, handleWidgetExitAnimation, isAnimating };
};

export default useWidgetAnimation;
